import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Save, ArrowLeft, Plus, X, Image as ImageIcon, Upload, Trash2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import FormInput from '../../components/FormInput';
import LoadingSpinner from '../../components/LoadingSpinner';
import * as pkgApi from '../../api/packages.api';
import { errorMessage } from '../../api/client';

const empty = { name: '', description: '', price: '', currency: 'MYR', isActive: true, tests: [] };

function PackageForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(empty);
  const [test, setTest] = useState('');
  const [loading, setLoading] = useState(editing);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  // Banner state. For new packages, we keep the chosen file locally and upload
  // it after the package is created. For existing packages, we upload right away.
  const [pkgRecord, setPkgRecord] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');
  const [bannerBusy, setBannerBusy] = useState(false);
  const bannerInputRef = useRef(null);

  useEffect(() => {
    if (!editing) return;
    pkgApi.getPackage(id)
      .then((p) => {
        setForm({
          name: p.name || '',
          description: p.description || '',
          price: p.price ?? '',
          currency: p.currency || 'MYR',
          isActive: p.isActive ?? true,
          tests: p.tests || []
        });
        setPkgRecord(p);
      })
      .catch((e) => setErr(errorMessage(e)))
      .finally(() => setLoading(false));
  }, [editing, id]);

  useEffect(() => {
    if (!bannerFile) return undefined;
    const url = URL.createObjectURL(bannerFile);
    setBannerPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [bannerFile]);

  function set(k, v) { setForm((s) => ({ ...s, [k]: v })); }

  function onPickBanner(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    setBannerFile(file);
  }

  async function uploadBannerNow(packageId) {
    if (!bannerFile) return;
    setBannerBusy(true);
    try {
      const updated = await pkgApi.uploadPackageBanner(packageId, bannerFile);
      setPkgRecord(updated);
      setBannerFile(null);
      setBannerPreview('');
      toast.success('Banner uploaded');
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBannerBusy(false);
    }
  }

  async function removeBanner() {
    if (!pkgRecord?._id) {
      setBannerFile(null);
      setBannerPreview('');
      if (bannerInputRef.current) bannerInputRef.current.value = '';
      return;
    }
    if (!window.confirm('Remove banner image?')) return;
    setBannerBusy(true);
    try {
      const updated = await pkgApi.deletePackageBanner(pkgRecord._id);
      setPkgRecord(updated);
      setBannerFile(null);
      setBannerPreview('');
      if (bannerInputRef.current) bannerInputRef.current.value = '';
      toast.success('Banner removed');
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBannerBusy(false);
    }
  }

  function addTest() {
    const t = test.trim();
    if (!t) return;
    if (!form.tests.includes(t)) set('tests', [...form.tests, t]);
    setTest('');
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr('');
    setSubmitting(true);
    try {
      const payload = { ...form, price: Number(form.price) };
      let saved;
      if (editing) {
        saved = await pkgApi.updatePackage(id, payload);
        toast.success('Package updated');
      } else {
        saved = await pkgApi.createPackage(payload);
        toast.success('Package created');
      }
      if (bannerFile && saved?._id) {
        try {
          await pkgApi.uploadPackageBanner(saved._id, bannerFile);
        } catch (e2) {
          toast.error(`Saved, but banner upload failed: ${errorMessage(e2)}`);
        }
      }
      navigate('/packages');
    } catch (e1) {
      setErr(errorMessage(e1));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: 'Packages', to: '/packages' }, { label: editing ? 'Edit package' : 'New package' }]}
        title={editing ? 'Edit package' : 'New package'}
        actions={
          <button className="btn" type="button" onClick={() => navigate('/packages')}>
            <ArrowLeft size={14} /> Back
          </button>
        }
      />

      <form onSubmit={onSubmit} className="card" style={{ maxWidth: 800 }}>
        <FormInput
          label="Name"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          placeholder="e.g. Comprehensive Wellness Screening"
          required
        />
        <FormInput
          label="Description"
          textarea
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          placeholder="Briefly describe what's covered…"
        />

        <div className="grid grid-2">
          <FormInput
            label="Price"
            type="number"
            step="0.01"
            value={form.price}
            onChange={(e) => set('price', e.target.value)}
            required
          />
          <FormInput
            label="Currency"
            value={form.currency}
            onChange={(e) => set('currency', e.target.value.toUpperCase())}
            placeholder="MYR"
          />
        </div>

        <div className="field">
          <label>Tests included</label>
          <div className="row" style={{ gap: 8 }}>
            <input
              className="input"
              value={test}
              onChange={(e) => setTest(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTest(); } }}
              placeholder="Add a test (press Enter)"
            />
            <button type="button" className="btn btn-outline" onClick={addTest}><Plus size={14} /> Add</button>
          </div>
          <div className="tag-stack" style={{ marginTop: 10 }}>
            {form.tests.map((t) => (
              <span key={t} className="badge brand">
                {t}
                <button
                  type="button"
                  onClick={() => set('tests', form.tests.filter((x) => x !== t))}
                  style={{ background: 'transparent', border: 0, marginLeft: 6, cursor: 'pointer', color: 'inherit' }}
                  aria-label={`Remove ${t}`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className="field">
          <label>Banner image</label>
          <BannerPicker
            editing={editing}
            pkg={pkgRecord}
            bannerFile={bannerFile}
            bannerPreview={bannerPreview}
            bannerBusy={bannerBusy}
            inputRef={bannerInputRef}
            onPick={onPickBanner}
            onUploadNow={() => pkgRecord?._id && uploadBannerNow(pkgRecord._id)}
            onClearChoice={() => { setBannerFile(null); setBannerPreview(''); if (bannerInputRef.current) bannerInputRef.current.value = ''; }}
            onRemove={removeBanner}
          />
          <div className="help" style={{ marginTop: 6, fontSize: 12, color: 'var(--muted)' }}>
            Shown to patients on the treatment-plan detail page. PNG or JPG, up to 10 MB.
          </div>
        </div>

        <div className="field">
          <label className="row">
            <input type="checkbox" checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} />
            <span>Active (visible to patients)</span>
          </label>
        </div>

        {err && <div className="notice warning">{err}</div>}

        <div className="row" style={{ marginTop: 'var(--s-4)' }}>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? <span className="spinner" /> : <Save size={16} />}
            {editing ? 'Save changes' : 'Create package'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => navigate('/packages')}>Cancel</button>
        </div>
      </form>
    </>
  );
}

function BannerPicker({
  editing,
  pkg,
  bannerFile,
  bannerPreview,
  bannerBusy,
  inputRef,
  onPick,
  onUploadNow,
  onClearChoice,
  onRemove,
}) {
  const existingUrl = pkg?.bannerUrl ? pkgApi.packageBannerUrl(pkg) : '';
  const previewSrc = bannerPreview || existingUrl;
  const hasPreview = Boolean(previewSrc);

  return (
    <div>
      <div
        style={{
          border: '1px dashed var(--border)',
          borderRadius: 10,
          padding: hasPreview ? 8 : 16,
          background: 'var(--surface-muted, #fafafa)',
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {hasPreview ? (
          <img
            src={previewSrc}
            alt="Banner preview"
            style={{ width: 220, height: 120, objectFit: 'cover', borderRadius: 8, background: '#eee' }}
          />
        ) : (
          <div
            style={{
              width: 220, height: 120, borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#eee', color: '#888',
            }}
          >
            <ImageIcon size={28} />
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={onPick}
            style={{ display: 'none' }}
          />
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => inputRef.current?.click()}
              disabled={bannerBusy}
            >
              <Upload size={14} /> {existingUrl || bannerFile ? 'Replace' : 'Choose image'}
            </button>

            {bannerFile && editing && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={onUploadNow}
                disabled={bannerBusy || !pkg?._id}
              >
                {bannerBusy ? <span className="spinner" /> : <Save size={14} />} Upload now
              </button>
            )}

            {bannerFile && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onClearChoice}
                disabled={bannerBusy}
              >
                <X size={14} /> Discard choice
              </button>
            )}

            {existingUrl && !bannerFile && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onRemove}
                disabled={bannerBusy}
                style={{ color: 'var(--danger, #b00)' }}
              >
                <Trash2 size={14} /> Remove
              </button>
            )}
          </div>
          {bannerFile && (
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              {bannerFile.name} — {(bannerFile.size / 1024).toFixed(0)} KB
              {!editing && ' · will upload after the package is created'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PackageForm;
